import type { PageBlock } from "@/utils/customPageStore";
import HeroSectionBlock from "./HeroSectionBlock";
import InfoCardsBlock from "./InfoCardsBlock";
import SpaceGridBlock from "./SpaceGridBlock";
import GradientQuoteBlock from "./GradientQuoteBlock";
import CTABannerBlock from "./CTABannerBlock";
import FounderStoryBlock from "./FounderStoryBlock";
import ArtistHeadingBlock from "./ArtistHeadingBlock";
import FAQAccordionBlock from "./FAQAccordionBlock";
import ContactInfoBlock from "./ContactInfoBlock";

interface SectionBlockRendererProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function SectionBlockRenderer({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: SectionBlockRendererProps) {
  const meta = block.sectionMeta;
  const layout = meta?.layout ?? "";

  const common = {
    block,
    isAdmin,
    isDark,
    headingFont,
    headingStyle,
    onDelete,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    onUpdate,
  };

  switch (layout) {
    case "hero": return <HeroSectionBlock {...common} />;
    case "info-cards": return <InfoCardsBlock {...common} />;
    case "space-grid": return <SpaceGridBlock {...common} />;
    case "gradient-quote": return <GradientQuoteBlock {...common} />;
    case "cta-banner": return <CTABannerBlock {...common} />;
    case "founder-story": return <FounderStoryBlock {...common} />;
    case "artist-heading": return <ArtistHeadingBlock {...common} />;
    case "faq-accordion": return <FAQAccordionBlock {...common} />;
    case "contact-info": return <ContactInfoBlock {...common} />;
    default:
      return (
        <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
          {isAdmin && (
            <div className={`flex items-center justify-between mb-2 rounded-lg px-2 py-1.5 ${isDark ? "bg-gray-800/80 border border-gray-700" : "bg-green-50 border border-green-200"}`}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-green-600">Imported: {layout}</span>
              <button onClick={onDelete} className="flex items-center gap-1 px-2.5 py-1 rounded-md cursor-pointer text-xs font-bold whitespace-nowrap bg-red-500 text-white hover:bg-red-600">
                <i className="ri-delete-bin-line text-xs"></i> Delete
              </button>
            </div>
          )}
          <div className={`rounded-2xl p-8 text-center border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
            <i className="ri-layout-masonry-line text-gray-400 text-3xl mb-2 block"></i>
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{block.content}</p>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>Layout: {layout}</p>
          </div>
        </div>
      );
  }
}